import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Award, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ISO-2 → flag emoji
const flagEmoji = (iso) => {
  if (!iso || iso.length !== 2) return '';
  const A = 0x1F1E6;
  const codes = iso.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65));
  try { return String.fromCodePoint(...codes); } catch { return ''; }
};

/** Normalize legacy absolute preview URLs → same-origin /api/files/... path. */
const normalizeImg = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/files/')) return `${BACKEND_URL}${url}`;
  const idx = url.indexOf('/api/files/');
  if (idx > 0 && (url.startsWith('http://') || url.startsWith('https://'))) {
    return `${BACKEND_URL}${url.slice(idx)}`;
  }
  return url;
};

const RAW_LABELS = {
  goals: '⚽ Golos', assists: '🎯 Assistências',
  shots_on_target: '✅ Remates à baliza', shots_missed: '❌ Remates fora',
  passes_completed: '✅ Passes certos', passes_failed: '❌ Passes errados',
  tackles: '🦵 Desarmes', blocks: '🧱 Bloqueios',
  clean_sheets: '🛡️ JSG', saves: '🧤 Defesas (GR)',
  own_goals: '🥅 Golos contra', wins: '🏆 Vitórias', draws: '🤝 Empates',
  fouls_committed: '🟨 Faltas', fouls_suffered: '💢 Faltas sofridas',
  games_played: '🕐 Jogos', dribbles: '💫 Fintas',
  interceptions: '🧤 Interceções', goals_conceded: '🥅 Golos sofridos',
};

// Card visual tier based on rating
const getTier = (r) => {
  if (r >= 85) return {
    name: 'totw',
    bg: 'linear-gradient(160deg, #0b3a4a 0%, #0e6473 35%, #1d8fa3 65%, #f6e600 100%)',
    border: '#0e6473',
    accent: '#f6e600',
    text: '#f6e600',
    nameColor: '#f6e600',
  };
  if (r >= 70) return {
    name: 'gold',
    bg: 'linear-gradient(160deg, #b58729 0%, #d8a73a 30%, #f3d765 55%, #d8a73a 85%, #8e6010 100%)',
    border: '#8e6010',
    accent: '#5b3d05',
    text: '#3a2602',
    nameColor: '#3a2602',
  };
  return {
    name: 'silver',
    bg: 'linear-gradient(160deg, #828588 0%, #c8cdd1 30%, #eef0f2 55%, #b9bcc0 85%, #5e6164 100%)',
    border: '#5e6164',
    accent: '#1f2225',
    text: '#1f2225',
    nameColor: '#1f2225',
  };
};

const Attr = ({ k, label, value, color }) => (
  <div className="flex items-baseline gap-1.5 leading-none">
    <span className="text-2xl font-black tabular-nums" style={{ color }}>{value}</span>
    <span className="text-xs font-black tracking-widest" style={{ color }}>{k}</span>
  </div>
);

const PlayerCard = ({ playerName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API}/players/${encodeURIComponent(playerName)}/card`)
      .then(r => { if (live) { setData(r.data); setLoading(false); } })
      .catch(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [playerName]);

  if (!playerName) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="player-card-modal"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="close-player-card"
        aria-label="Fechar"
      >
        <X size={24} />
      </button>

      {loading ? (
        <div className="text-white text-lg font-bold">A carregar carta…</div>
      ) : !data ? (
        <div className="text-white text-lg font-bold">Jogador não encontrado</div>
      ) : (
        <div
          className="w-full max-w-[340px] sm:max-w-[380px] aspect-[3/4]"
          style={{ perspective: '1200px' }}
          onClick={(e) => { e.stopPropagation(); setFlipped(!flipped); }}
          data-testid="player-card-flip"
        >
          <div
            className="relative w-full h-full transition-transform duration-700 cursor-pointer"
            style={{
              transformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            <PlayerCardFace data={data} />
            <PlayerCardBack data={data} />
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// PUBLIC FACE — reusable card front face driven by a `data` prop (no fetch).
// Used by the Fantasy pitch/picker tiles, scaled via CSS transform.
// =========================================================================
export const PlayerCardFace = ({ data }) => {
  if (!data) return null;
  const tier = getTier(data.rating);
  const flag = flagEmoji(data.nationality) || data.nationality || '';
  const cardImg = normalizeImg(data.card_image_url || '');
  const hasBg = !!cardImg;
  const overlayShadow = '0 2px 8px rgba(0,0,0,.85), 0 0 2px rgba(0,0,0,.9)';

  return (
    <div
      className="absolute inset-0 rounded-[28px] overflow-hidden flex flex-col"
      style={{
        background: tier.bg,
        boxShadow: `0 12px 40px rgba(0,0,0,.45), inset 0 0 0 3px ${tier.accent}`,
        backfaceVisibility: 'hidden',
      }}
    >
      {/* Background image (full bleed) */}
      {hasBg && (
        <img
          src={cardImg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
      )}
      {/* Vignette */}
      {hasBg && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 88%, rgba(0,0,0,0.7) 100%)',
          }}
        />
      )}

      {/* Top row: rating / position / flag */}
      <div className="flex items-start justify-between p-5 z-20 flex-shrink-0">
        <div className="flex flex-col items-center leading-none gap-1">
          <span className="text-5xl font-black tabular-nums" style={{ color: hasBg ? '#FFFFFF' : tier.text, textShadow: hasBg ? overlayShadow : '2px 2px 0 rgba(0,0,0,.15)' }}>
            {data.rating}
          </span>
          <span className="text-base font-black tracking-widest" style={{ color: hasBg ? '#FFFFFF' : tier.text, textShadow: hasBg ? overlayShadow : undefined }}>
            {data.position_short}
          </span>
          {flag && (
            <span className="text-3xl mt-1 leading-none" style={{ filter: hasBg ? 'drop-shadow(0 2px 4px rgba(0,0,0,.6))' : undefined }}>
              {flag}
            </span>
          )}
        </div>
      </div>

      {/* Middle: fallback initial when no bg */}
      {!hasBg && (
        <div className="flex-1 flex items-end justify-center min-h-0 px-4 pb-2 relative">
          <span className="text-[10rem] font-black leading-none select-none pb-4" style={{ color: tier.text, opacity: 0.18 }}>
            {data.name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {/* Bottom: name + attributes */}
      <div
        className="px-5 pt-3 pb-5 z-20 flex-shrink-0 mt-auto"
        style={{
          background: hasBg
            ? 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%)'
            : 'linear-gradient(to top, rgba(0,0,0,0.18), rgba(0,0,0,0))',
        }}
      >
        <p className="text-2xl font-black uppercase text-center tracking-tight truncate" style={{ color: hasBg ? '#FFFFFF' : tier.nameColor, textShadow: hasBg ? overlayShadow : undefined }}>
          {data.name}
        </p>
        <div className="h-[2px] my-2 opacity-50" style={{ background: hasBg ? '#FFFFFF' : tier.text }} />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 px-2">
          {Object.entries(data.attributes || {}).map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-1.5 leading-none">
              <span className="text-2xl font-black tabular-nums" style={{ color: hasBg ? '#FFFFFF' : tier.text, textShadow: hasBg ? overlayShadow : undefined }}>
                {v}
              </span>
              <span className="text-xs font-black tracking-widest" style={{ color: hasBg ? '#FFFFFF' : tier.text, textShadow: hasBg ? overlayShadow : undefined }}>
                {k}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Internal back-side kept private (only used by the modal PlayerCard).
const PlayerCardBack = ({ data }) => {
  if (!data) return null;
  const tier = getTier(data.rating);
  return (
    <div
      className="absolute inset-0 rounded-[28px] overflow-hidden flex flex-col p-5"
      style={{
        background: tier.bg,
        boxShadow: `0 12px 40px rgba(0,0,0,.4), inset 0 0 0 2px ${tier.border}`,
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
      }}
    >
      <p className="text-xs uppercase tracking-widest text-center mb-2" style={{ color: tier.text, opacity: 0.7 }}>Estatísticas do mês</p>
      <p className="text-xl font-black uppercase text-center tracking-tight" style={{ color: tier.nameColor }}>{data.name}</p>
      <p className="text-center text-xs font-bold mb-3" style={{ color: tier.text }}>
        Pontos_Rating: <span className="font-black">{Number(data.pontos_rating_monthly).toFixed(1)}</span>
        {' · '}Total: <span className="font-black">{Number(data.pontos_rating_total).toFixed(1)}</span>
      </p>
      <div className="flex-1 overflow-y-auto rounded-lg bg-black/15 p-3 backdrop-blur-sm">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {Object.entries(data.raw_stats || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px] font-bold" style={{ color: tier.text }}>
              <span className="truncate">{RAW_LABELS[k] || k}</span>
              <span className="font-black tabular-nums ml-2">{v}</span>
            </div>
          ))}
        </div>
        {data.titles?.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: tier.text + '40' }}>
            <p className="text-[10px] uppercase tracking-widest font-black mb-1" style={{ color: tier.text }}>
              <Award size={12} className="inline -mt-1" /> Títulos ({data.titles.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {data.titles.map((t, i) => (
                <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: tier.text + '20', color: tier.text }}>{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-center mt-3 opacity-70" style={{ color: tier.text }}>
        <RotateCcw size={10} className="inline -mt-0.5" /> Toca para voltar
      </p>
    </div>
  );
};

export default PlayerCard;

